// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState, useEffect, useCallback } from 'react';
import { getStaging } from '../services/coverageService';
import type { StagingSummaryResponse } from '../types';

/** How often to poll staging while the review step is visible. Publish
 * targets move through pending/done/failed asynchronously after decisions
 * are applied, so the cards refresh in the background. */
const POLL_INTERVAL_MS = 10_000;

interface UseCoverageStagingResult {
  staging: StagingSummaryResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useCoverageStaging(
  siteId: string | undefined,
  sessionId: string | undefined,
  enabled = true
): UseCoverageStagingResult {
  const [staging, setStaging] = useState<StagingSummaryResponse | null>(null);
  const [loading, setLoading] = useState(enabled && Boolean(siteId && sessionId));
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey(k => k + 1), []);

  useEffect(() => {
    if (!enabled || !siteId || !sessionId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Background polls never flash the spinner or replace good data with a
    // transient-error banner (same pattern as useApprovals).
    const load = async (isPoll: boolean) => {
      try {
        if (!isPoll) {
          setLoading(true);
          setError(null);
        }
        const result = await getStaging(siteId, sessionId);
        if (cancelled) return;
        setStaging(result);
        if (isPoll) setError(null);
      } catch (err) {
        if (cancelled || isPoll) return;
        setError((err as Error).message ?? 'Failed to load staging summary');
      } finally {
        if (!cancelled && !isPoll) setLoading(false);
      }
    };

    load(false);
    const intervalId = setInterval(() => load(true), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [siteId, sessionId, enabled, fetchKey]);

  return { staging, loading, error, refetch };
}
